import React from 'react';

interface MarkdownContentProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * MarkdownContent - Wrapper component for rendering markdown-style documentation content
 *
 * Provides consistent styling for:
 * - Headings (h1-h6)
 * - Paragraphs
 * - Lists (ordered and unordered)
 * - Code blocks and inline code
 * - Tables
 * - Links
 * - Blockquotes
 * - Horizontal rules
 *
 * Supports dark mode automatically via Tailwind's prose-invert
 */
export const MarkdownContent: React.FC<MarkdownContentProps> = ({ children, className = '' }) => {
  return (
    <div
      className={`
        prose prose-slate dark:prose-invert max-w-none
        prose-headings:scroll-mt-20
        prose-h1:text-4xl prose-h1:font-bold prose-h1:tracking-tight prose-h1:mb-8
        prose-h2:text-3xl prose-h2:font-semibold prose-h2:mt-10 prose-h2:mb-6
        prose-h3:text-2xl prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-5
        prose-h4:text-xl prose-h4:font-semibold prose-h4:mt-6 prose-h4:mb-4
        prose-p:text-base prose-p:leading-7 prose-p:mb-6
        prose-a:text-primary prose-a:no-underline hover:prose-a:underline
        prose-strong:text-foreground prose-strong:font-semibold
        prose-code:text-sm prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md
        prose-code:before:content-none prose-code:after:content-none
        prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:my-6
        prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6
        prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6
        prose-li:my-3 prose-li:leading-relaxed
        prose-li>ul:mt-3 prose-li>ol:mt-3
        prose-table:border-collapse prose-table:w-full prose-table:my-8
        prose-th:border prose-th:border-border prose-th:bg-muted prose-th:px-4 prose-th:py-2 prose-th:text-left
        prose-td:border prose-td:border-border prose-td:px-4 prose-td:py-2
        prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:my-6
        prose-hr:border-border prose-hr:my-12
        prose-img:rounded-lg prose-img:shadow-md prose-img:my-8
        ${className}
      `}
    >
      {children}
    </div>
  );
};
